import React, { useEffect, useRef, useState } from "react";
import { Crosshair } from "lucide-react";

// Bare widget UUID from the Elfsight dashboard (public embed id, not a credential).
// Accepts either a bare UUID or an accidental full "elfsight-app-<uuid>" paste.
const RAW = (process.env.REACT_APP_ELFSIGHT_SOCIAL_WIDGET_ID || process.env.REACT_APP_ELFSIGHT || "").trim();
const WIDGET_ID = RAW.replace(/^elfsight-app-/, "");
// Elfsight's current CDN. (Old host was https://static.elfsight.com/platform/platform.js.)
const SCRIPT_SRC = "https://elfsightcdn.com/platform.js";

function FallbackHero() {
  return (
    <div className="relative border border-sniper bg-surface px-8 py-[34px] text-center" data-testid="social-fallback">
      {/* all four scope corners (never a lopsided 2/4 frame) */}
      <span className="pointer-events-none absolute top-1.5 left-1.5 w-3.5 h-3.5 border-t border-l border-[hsl(var(--scope-corner))]" />
      <span className="pointer-events-none absolute top-1.5 right-1.5 w-3.5 h-3.5 border-t border-r border-[hsl(var(--scope-corner))]" />
      <span className="pointer-events-none absolute bottom-1.5 left-1.5 w-3.5 h-3.5 border-b border-l border-[hsl(var(--scope-corner))]" />
      <span className="pointer-events-none absolute bottom-1.5 right-1.5 w-3.5 h-3.5 border-b border-r border-[hsl(var(--scope-corner))]" />
      <Crosshair className="w-8 h-8 text-volt mx-auto" strokeWidth={1.4} />
      <h2 className="font-heading text-[19px] font-semibold uppercase tracking-tight mt-3">Follow OpticSwap</h2>
      <p className="text-[14px] font-body text-secondary-foreground mt-1.5 max-w-[42ch] mx-auto">
        Fresh drops, deal alerts, and glass-nerd content. Our live social feed is being dialed in — follow us in the meantime.
      </p>
      <div className="flex items-center justify-center gap-3 mt-5">
        <a href="https://instagram.com/opticswap" target="_blank" rel="noopener noreferrer" data-testid="social-ig"
          className="inline-flex items-center border border-sniper hover:border-olive hover:text-olive-bright text-foreground font-mono uppercase text-[10.5px] tracking-[1.4px] px-4 py-2.5 transition-colors">
          Instagram
        </a>
        <a href="https://facebook.com/opticswap" target="_blank" rel="noopener noreferrer" data-testid="social-fb"
          className="inline-flex items-center border border-sniper hover:border-olive hover:text-olive-bright text-foreground font-mono uppercase text-[10.5px] tracking-[1.4px] px-4 py-2.5 transition-colors">
          Facebook
        </a>
      </div>
    </div>
  );
}

export default function ElfsightSocialFeed() {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ok | failed

  useEffect(() => {
    if (!WIDGET_ID) return;
    // Inject the platform script ONCE. Only ever runs on /social because this component
    // is mounted only by the Social page — never app-wide.
    let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onerror = () => setStatus("failed");
      document.body.appendChild(script);
    }
    // If the widget hasn't populated the container after a grace period (blocked script,
    // bad id, network), fall back to the follow card. Keeps the div mounted meanwhile so
    // Elfsight can render into it.
    const t = setTimeout(() => {
      const el = ref.current;
      setStatus(el && el.childElementCount > 0 ? "ok" : "failed");
    }, 7000);
    return () => clearTimeout(t);
  }, []);

  if (!WIDGET_ID || status === "failed") return <FallbackHero />;

  return (
    <div className="w-full max-w-full overflow-hidden" data-testid="elfsight-wrap">
      <div
        ref={ref}
        className={`elfsight-app-${WIDGET_ID}`}
        data-elfsight-app-lazy
        data-testid="elfsight-widget"
      />
    </div>
  );
}
